import React, { Suspense } from "react";
import { connect } from "react-redux";
import formatMoney from "../helpers/formatMoney";
import getPackagePart from "../helpers/getPackagePart";
import groupChoicesByPageId from "../helpers/groupChoicesByPageId";
import { RootState } from "../state/reducers";
import { ChoiceState, getTotalPrice } from "../state/reducers/choiceReducer";
import { FormData, ID, Part } from "../types";
import PartPrice from "./PartPrice";

const SummaryButtons = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pdf-btns" */
      "./SummaryButtons"
    )
);

interface SummaryPageProps {
  choices: ChoiceState;
  thePackage: (id: ID) => Part | undefined;
  total: (withDiscount: boolean) => number;
  formData: FormData;
}

const SummaryPage: React.FC<SummaryPageProps> = ({
  choices,
  thePackage,
  total,
  formData,
}) => {
  const grouped = groupChoicesByPageId(choices);
  const title = "post_title" in formData.post ? formData.post.post_title : "";
  const photoDisclaimer =
    "disclaimers" in formData.post
      ? formData.post.disclaimers.summary_photo_disclaimer
      : "";
  const aaPrice = total(true);
  const msrpPrice = total(false);
  return (
    <div className="page summary-page">
      <header>
        <h2>{title} Summary</h2>
        <div className="summary-header-container">
          <div className="summary-image">
            <img src={formData.image} />
            <p className="disclaimer">{photoDisclaimer}</p>
          </div>
          <div className="summary-total-price-container">
            {aaPrice !== 0 ? (
              <>
                <span className="summary-total-price-label">Total</span>
                <span className="summary-total-price">
                  {formatMoney(aaPrice)}
                  <span>Avid Advantage Price</span>
                </span>
              </>
            ) : null}
            {msrpPrice !== 0 ? (
              <span className="summary-full-price">
                {formatMoney(msrpPrice)}
                <span>MSRP</span>
              </span>
            ) : null}
          </div>
        </div>
      </header>
      <div className="summary-details">
        <h3>Your Details</h3>
        <p>These are the details of your specific build.</p>

        {formData.entities.pages.allIds.map((id) => {
          const pageItem = formData.entities.pages.byId[id];
          const choicesGroupedByPage = grouped[id];

          if (!choicesGroupedByPage) return null;
          if (!pageItem) return null;

          return (
            <div key={id} className="summary-part-details">
              <span className="summary-group">{pageItem.short}</span>
              <ul className="summary-parts">
                {choicesGroupedByPage.map((item) => {
                  const section =
                    formData.entities.sections.byId[item.sectionId];
                  if (!section) return null;
                  return (
                    <li key={item.id} className="summary-part">
                      <span className="summary-part-label">
                        <span>
                          {pageItem.short === "colors"
                            ? `${section.label} - ${item.label}`
                            : item.label}
                        </span>
                        {item.quantity > 1 ? (
                          <strong>{` x ${item.quantity}`}</strong>
                        ) : (
                          <></>
                        )}
                      </span>
                      <PartPrice
                        thePackage={thePackage(item.id)}
                        price={item.price}
                        discount={item.discount}
                        mainClass={"summary-part-price"}
                        packagedClass={"summary-part-included"}
                        discountClass={"summary-part-price-discount"}
                        fullClass={"summary-part-price-full"}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      <h3 className="footer-heading">Next Steps</h3>

      <Suspense fallback={<div>Loading...</div>}>
        <SummaryButtons />
      </Suspense>
    </div>
  );
};

export default connect((state: RootState) => {
  const thePackage = getPackagePart(state.choices, state.packaged);
  return {
    choices: state.choices,
    thePackage,
    total: getTotalPrice(state),
    formData: state.formData,
  };
}, {})(SummaryPage);
